// The guides (components/AppTour.jsx). Two, kept apart:
//  - "member": the first-run guide, written from an ordinary member's point of
//    view (nothing here needs edit rights) and shown once to everyone, team
//    leads included. Replayed from the account menu's "How to use the app".
//  - "leader": extra, for team leads who want it - creating events, setlists,
//    approving songs, templates, inviting people. Never started automatically:
//    leads open it from the account menu's "How to manage the team", which only
//    they see.
// Whether someone has been shown the member guide is remembered on their
// account (User.tour_seen_version on the server, delivered with the user
// object), so it follows them across devices and the installed app. A flag in
// this browser backs it up for the moment before the server copy is saved or
// if saving fails. Bump TOUR_VERSION to show an updated guide to everyone again.
export const TOUR_VERSION = 1;

// Fired by the account menu; components/AppTour.jsx listens. detail.kind says
// which guide ("member" by default).
export const START_TOUR_EVENT = "planpraise:start-tour";

const localKey = (userId) => `tourSeen:v${TOUR_VERSION}:${userId}`;

const seenInThisBrowser = (userId) => {
  try {
    return localStorage.getItem(localKey(userId)) === "1";
  } catch (e) {
    return false;
  }
};

export const markSeenInThisBrowser = (userId) => {
  try {
    localStorage.setItem(localKey(userId), "1");
  } catch (e) {
    // Storage blocked: the server copy is the one that counts.
  }
};

// `user` is the app's user object. The server's number wins; a cached user from
// before the field existed has none, so this browser's flag stands in.
export const hasSeenTour = (user) =>
  (typeof user.tour_seen_version === "number" && user.tour_seen_version >= TOUR_VERSION) ||
  seenInThisBrowser(user.id);

// Pages with the main navigation on them - where the tour can point at it.
export const TOUR_PAGES = ["/song-proposals", "/upcoming", "/calendar", "/catalog"];

const nav = (name) => `[data-tour="${name}"]`;

const centered = (title, content) => ({
  target: "body",
  placement: "center",
  disableBeacon: true,
  title,
  content,
});

const onNav = (name, placement, title, content) => ({
  target: nav(name),
  placement,
  disableBeacon: true,
  title,
  content,
});

// `kind`: "member" (default) or "leader".
// `ios`: iPhones/iPads can install the app but can't be a share target.
// `standalone`: already running as the installed app, so skip the install step.
// `isLeader`: only used to point team leads at their own guide from the last
// step of the member guide.
export const buildSteps = ({ ios, standalone, largeScreen, kind = "member", isLeader = false }) => {
  if (kind === "leader") return buildLeaderSteps({ largeScreen });
  const navPlacement = largeScreen ? "bottom" : "top";
  const steps = [
    centered(
      "Welcome to PlanPraise",
      "A quick guide to the app - under a minute. Skip it now if you like; you can open it again any time from the account menu under \"How to use the app\"."
    ),
    onNav("nav-proposals", navPlacement, "Song Proposals", "Suggest songs for the team to use, and see what others have suggested."),
    onNav(
      "nav-upcoming",
      navPlacement,
      "Upcoming",
      "The events coming up, with their songs and lyrics. A dot appears when one is new or changed."
    ),
    onNav("nav-calendar", navPlacement, "Calendar", "Every event on a month view. Tap one to open it."),
    centered(
      "Inside an event",
      "You'll see the songs in order with their lyrics. \"Play full set\" plays the YouTube versions one after another, and Share sends the setlist to your team's chat."
    ),
    onNav(
      "nav-catalog",
      navPlacement,
      "Catalog",
      "Every song the team has, searchable. Songs still missing lyrics carry a \"No lyrics\" flag."
    ),
  ];

  if (!standalone) {
    steps.push(
      centered(
        "Put it on your home screen",
        ios
          ? "Install PlanPraise like an app: tap the Share button in Safari, then \"Add to Home Screen\"."
          : "Install PlanPraise like an app: use the Install button when it appears, or your browser menu's \"Install app\" / \"Add to Home screen\". It opens faster and can notify you about new events."
      )
    );
  }

  steps.push(
    centered(
      "Share songs straight in",
      ios
        ? "Found a song on YouTube or Spotify? Copy its link, then in Song Proposals add a song and paste it - the title, artist and lyrics fill in for you."
        : "Found a song on YouTube or Spotify? Tap Share and pick PlanPraise (once installed). The new-song form opens with the link in and the title, artist and lyrics filled in for you."
    ),
    centered(
      "Stay in the loop",
      "Turn on notifications under Account (in the account menu) to hear about new or changed events. Your phone or browser will ask for permission first."
    ),
    onNav(
      "account-menu",
      "bottom",
      "Your account menu",
      isLeader
        ? "Notifications and your settings live here - and \"How to use the app\" if you want to see this guide again. As a team lead, \"How to manage the team\" has the extra tools guide whenever you want it."
        : "Notifications and your settings live here - and \"How to use the app\" if you want to see this guide again."
    )
  );

  return steps;
};

// The team-lead guide. Only team leads can open it, so it can talk about edit
// controls. Pages other than the four with the main navigation aren't open
// while it runs, so the steps point at the navigation and explain the rest.
const buildLeaderSteps = ({ largeScreen }) => {
  const navPlacement = largeScreen ? "bottom" : "top";
  return [
    centered(
      "Team lead tools",
      "A short guide to what you can do as a team lead. Skip it if you like; open it again any time from the account menu under \"How to manage the team\"."
    ),
    onNav(
      "nav-calendar",
      navPlacement,
      "Create an event",
      "Open the Calendar and tap the + button, or tap a day. A new event starts as a Draft that only team leads can see."
    ),
    centered(
      "Build the setlist",
      "Open an event and press Edit. Add songs and text blocks, drag them into order, and pick a setlist template to group them into sections. Press Done when you're finished."
    ),
    centered(
      "Publish when it's ready",
      "Set the status to Published and the team sees it and is notified. Draft keeps it to team leads only. Cancelled keeps it visible but marked as cancelled, so people find out it's off."
    ),
    centered(
      "Tell the team about changes",
      "Changes to a published event are announced automatically (grouped, so nobody is spammed). Tick \"Notify team on next save\" to send a notification for certain."
    ),
    onNav(
      "nav-proposals",
      navPlacement,
      "Review song suggestions",
      "On a suggestion, open its menu and choose \"Add to catalog\" to approve it, or Reject. You can edit it first."
    ),
    onNav(
      "nav-catalog",
      navPlacement,
      "Add songs directly",
      "In the Catalog you can add a song straight in and fix its lyrics. Songs flagged \"No lyrics\" are the ones to look at."
    ),
    largeScreen
      ? onNav(
          "nav-templates",
          "bottom",
          "Setlist templates",
          "Templates are reusable sections for a setlist, like Opening and Worship. Pick one on an event to group its songs, and mark one as the default for new events."
        )
      : centered(
          "Setlist templates",
          "Templates are reusable sections for a setlist, like Opening and Worship. You'll find them in the account menu. Pick one on an event to group its songs, and mark one as the default for new events."
        ),
    onNav(
      "account-menu",
      "bottom",
      "Manage your team",
      "Open the account menu and choose Team. \"Invite a Friend\" makes a join link (good for 7 days), and you can make other people team leads or remove members."
    ),
  ];
};

// After a team lead finishes one guide: which other one to offer, if any.
// `taken` is the guides already run in this sitting, so the offer is made once
// (never again after they've done both) and never to anyone who isn't a lead.
export const guideToOffer = ({ isLeader, finished, taken }) => {
  if (!isLeader) return null;
  const other = finished === "leader" ? "member" : "leader";
  return taken.includes(other) ? null : other;
};
